// The developer's account: profile, sign-in security, credentials for scripts,
// appearance, and closing the account.
import { useEffect, useState } from "react";
import { Shell } from "../../components/Shell";
import {
  Button, Confirm, CopyButton, ErrorNotice, Field, Input, Loading, Modal, PageHead, Panel, SecretReveal, Segmented, Select, useToast,
} from "../../components/ui";
import { ProBadge, UpgradeActions, isPaying, planSummary } from "../../components/Upgrade";
import { describeAgent } from "../../lib/agent";
import { authApi, developerApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { navigate, useSearchParam } from "../../lib/router";
import { MIN_PASSWORD, useClient, useSession } from "../../lib/session";
import { getTheme, setTheme, type ThemePref } from "../../lib/theme";
import { formatDateTime, relativeTime } from "../../lib/time";
import type { Credential, DevSession, Developer } from "../../lib/types";
import { CONTACT_EMAIL } from "../../lib/site";

const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "plan", label: "Plan" },
  { id: "security", label: "Password & sign-ins" },
  { id: "credentials", label: "Developer credentials" },
  { id: "appearance", label: "Appearance" },
  { id: "close", label: "Close account" },
];

export function DevAccount() {
  const api = developerApi(useClient());
  const me = useQuery("dev:me", () => api.me());

  return (
    <Shell crumbs={[{ label: "Account" }]}>
      <PageHead title="Account" lede="Your profile, how you sign in, and the credentials your scripts use." />
      <nav className="settings-nav" aria-label="Account sections">
        {SECTIONS.map((s) => <a key={s.id} href={`#${s.id}`}>{s.label}</a>)}
      </nav>
      <ErrorNotice error={me.error} onRetry={me.reload} />
      {!me.data ? (
        me.loading && <Loading />
      ) : (
        <div className="stack">
          <ProfilePanel me={me.data} />
          <PlanPanel me={me.data} />
          <PasswordPanel me={me.data} />
          <SessionsPanel />
          <CredentialsPanel />
          <AppearancePanel />
          <ClosePanel me={me.data} />
        </div>
      )}
    </Shell>
  );
}

/**
 * Back from Stripe Checkout: thank them and wait for Stripe's webhook to turn
 * Pro on (usually a second or two), then tidy the address.
 */
function useCheckoutReturn(me: Developer) {
  const [billing, setBilling] = useSearchParam("billing");
  const toast = useToast();
  const [waiting, setWaiting] = useState(billing === "success");
  useEffect(() => {
    if (billing === "cancelled") {
      toast("Checkout cancelled. You haven't been charged.");
      setBilling("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billing]);
  useEffect(() => {
    if (!waiting) return;
    if (me.plan === "pro" && !me.plan_trial && me.billing) {
      setWaiting(false);
      setBilling("");
      toast("Payment received. Welcome to Pro!");
      return;
    }
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > 60_000) {
        window.clearInterval(id);
        setWaiting(false);
        setBilling("");
        return;
      }
      invalidate("dev:me");
    }, 2_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, me.plan, me.plan_trial, me.billing]);
  return waiting;
}

function PlanPanel({ me }: { me: Developer }) {
  const api = developerApi(useClient());
  const plans = useQuery("plans", () => api.plans());
  const confirming = useCheckoutReturn(me);
  const pro = me.plan === "pro";
  const features = plans.data?.features ?? [];
  return (
    <section id="plan" className="settings-section">
      <Panel title="Plan" sub="Paid features apply to every game on your account. Simulated money is free on every plan.">
        {confirming && <div className="notice" role="status" style={{ marginBottom: 16 }}>Payment received. Turning on Pro…</div>}
        {me.billing?.status === "past_due" && (
          <div className="notice warn" style={{ marginBottom: 16 }}>
            Your last payment didn't go through. Stripe will try again; update your card in Manage billing to keep Pro.
          </div>
        )}
        <div className="plan-card">
          <div className="grow">
            <div className="plan-name">{planSummary(me)} {pro && <ProBadge />}</div>
            {pro ? (
              <p className="muted small" style={{ margin: "6px 0 0" }}>
                {me.plan_trial
                  ? "You have every Pro feature until the trial ends, then you're back on Free. Nothing is deleted."
                  : me.billing?.cancel_at_period_end
                    ? "Your subscription is cancelled and won't renew. You keep Pro until the end of the period you paid for."
                    : "Thanks for supporting Game Banking Engine. Change your card, see invoices or cancel in Manage billing."}
              </p>
            ) : (
              <p className="muted small" style={{ margin: "6px 0 0" }}>
                Everything you need to build and run your game's economy. Pro adds:
              </p>
            )}
            {features.length > 0 && (
              <ul className="plan-features small">
                {features.map((f) => (
                  <li key={f.id}>
                    <strong>{f.name}</strong>: {f.description} {me.features?.includes(f.id) && <span className="badge ok">On</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {(!pro || me.plan_trial || isPaying(me)) && <UpgradeActions me={me} />}
        </div>
      </Panel>
    </section>
  );
}

function ProfilePanel({ me }: { me: Developer }) {
  const api = developerApi(useClient());
  const { rename } = useSession();
  const toast = useToast();
  const [name, setName] = useState(me.name);
  const save = useAction(() => api.updateMe({ name: name.trim() }));
  const changed = name.trim() !== me.name && name.trim() !== "";

  return (
    <section id="profile" className="settings-section">
      <Panel title="Profile" sub="How you appear in the console and in your games' audit logs.">
        <form
          className="row end"
          onSubmit={async (e) => {
            e.preventDefault();
            const d = await save.run();
            if (d) {
              rename(d.name);
              invalidate("dev:me");
              toast("Profile saved");
            }
          }}
        >
          <div className="grow" style={{ maxWidth: 420 }}>
            <Field label="Studio or your name">
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoComplete="organization" />
            </Field>
          </div>
          <Button type="submit" variant="primary" busy={save.pending} disabled={!changed}>Save</Button>
        </form>
        <ErrorNotice error={save.error} />
        <dl className="facts" style={{ marginTop: 20 }}>
          <dt>Email</dt>
          <dd>
            {me.contact_email}{" "}
            {me.email_verified ? <span className="badge ok">Verified</span> : <span className="badge">Not verified</span>}
            <div className="muted small">Your sign-in email. To change it, email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</div>
          </dd>
          <dt>Developer ID</dt>
          <dd><span className="mono">{me.id}</span> <CopyButton text={me.id} /></dd>
          <dt>Member since</dt>
          <dd>{formatDateTime(me.created_at)}</dd>
        </dl>
      </Panel>
    </section>
  );
}

function PasswordPanel({ me }: { me: Developer }) {
  const client = useClient();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const hasPassword = !!me.has_password;
  const mismatch = confirm !== "" && confirm !== next;
  const change = useAction(() =>
    authApi(client).changePassword(hasPassword ? { current_password: current, new_password: next } : { new_password: next }).then(() => true));

  return (
    <section id="security" className="settings-section">
      <Panel
        title={hasPassword ? "Password" : "Set a password"}
        sub={hasPassword
          ? "Changing it signs you out everywhere else."
          : `Set a password to sign in with ${me.contact_email} instead of pasting a credential.`}
      >
        <form
          className="password-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (mismatch) return;
            if (await change.run()) {
              setCurrent("");
              setNext("");
              setConfirm("");
              invalidate("dev:me");
              invalidate("dev:sessions");
              toast(hasPassword ? "Password changed. Your other sign-ins were ended." : "Password set. You can now sign in with your email.");
            }
          }}
        >
          {hasPassword && (
            <Field label="Current password">
              <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
            </Field>
          )}
          <Field label="New password" hint={`At least ${MIN_PASSWORD} characters.`}>
            <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} minLength={MIN_PASSWORD} maxLength={128} required />
          </Field>
          <Field label="Confirm new password" hint={mismatch ? "The passwords don't match." : undefined}>
            <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={mismatch || undefined} required />
          </Field>
          <div>
            <Button type="submit" variant="primary" busy={change.pending} disabled={mismatch}>{hasPassword ? "Change password" : "Set password"}</Button>
          </div>
        </form>
        <ErrorNotice error={change.error} />
      </Panel>
    </section>
  );
}

function SessionsPanel() {
  const api = developerApi(useClient());
  const { session, signOut } = useSession();
  const toast = useToast();
  const list = useQuery("dev:sessions", () => api.sessions());
  const [ending, setEnding] = useState<DevSession | null>(null);
  const end = useAction(async (s: DevSession) => {
    await api.revokeSession(s.id);
    return true;
  });
  const others = useAction(() => api.signOutOthers());
  const rows = list.data?.data ?? [];
  const otherCount = rows.filter((s) => !s.current).length;

  return (
    <Panel
      title="Where you're signed in"
      sub={session?.via === "credential"
        ? "Browsers signed in with your email and password. You're using a developer credential right now."
        : "Browsers signed in with your email and password. Sessions end after 7 days, or 3 hours idle in the console."}
      actions={otherCount > 0 ? (
        <Button size="small" busy={others.pending} onClick={async () => {
          const r = await others.run();
          if (r) {
            invalidate("dev:sessions");
            toast(`Signed out of ${r.sessions_ended} other ${r.sessions_ended === 1 ? "session" : "sessions"}`);
          }
        }}>Sign out everywhere else</Button>
      ) : undefined}
      flush
    >
      <ErrorNotice error={list.error ?? others.error} onRetry={list.reload} />
      {list.loading && !list.data && <Loading />}
      {list.data && rows.length === 0 && <p className="muted panel-body">No browser is signed in with your password.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Device</th><th>IP address</th><th>Last active</th><th>Signed in</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="cell-title">{describeAgent(s.user_agent)} {s.current && <span className="badge ok">This browser</span>}</div>
                  </td>
                  <td className="mono">{s.ip_address ?? "–"}</td>
                  <td>{s.current ? "Now" : relativeTime(s.last_used_at ?? s.created_at)}</td>
                  <td>{formatDateTime(s.created_at)}</td>
                  <td className="num"><Button size="small" onClick={() => setEnding(s)}>Sign out</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Confirm
        open={!!ending}
        title={ending?.current ? "Sign out of this browser?" : "Sign out this session?"}
        body={ending?.current ? "You'll go back to the sign-in page." : `${describeAgent(ending?.user_agent)} will need to sign in again.`}
        confirmLabel="Sign out"
        busy={end.pending}
        error={end.error}
        onConfirm={async () => {
          if (!ending) return;
          if (ending.current) {
            setEnding(null);
            signOut("You signed out of this browser.");
            return;
          }
          if (await end.run(ending)) {
            setEnding(null);
            invalidate("dev:sessions");
            toast("Signed out");
          }
        }}
        onClose={() => setEnding(null)}
      />
    </Panel>
  );
}

const EXPIRY = [
  { value: "", label: "Never expires" },
  { value: "30", label: "Expires in 30 days" },
  { value: "90", label: "Expires in 90 days" },
  { value: "365", label: "Expires in 1 year" },
];

function CredentialsPanel() {
  const api = developerApi(useClient());
  const { session } = useSession();
  const toast = useToast();
  const list = useQuery("dev:credentials", () => api.credentials());
  const [label, setLabel] = useState("");
  const [days, setDays] = useState("90");
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<Credential | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const issue = useAction(() =>
    api.issueCredential(label.trim() || "credential", days ? new Date(Date.now() + Number(days) * 86_400_000).toISOString() : undefined));
  const revoke = useAction(async (c: Credential) => {
    await api.revokeCredential(c.id);
    return true;
  });
  const isCurrent = (c: Credential) => session?.via === "credential" && session.token.startsWith(c.prefix.replace("…", ""));
  const all = list.data?.data ?? [];
  const live = all.filter((c) => !c.revoked_at && !(c.expires_at && Date.parse(c.expires_at) < Date.now()));
  const dead = all.filter((c) => !live.includes(c));
  const rows = showRevoked ? [...live, ...dead] : live;

  return (
    <section id="credentials" className="settings-section">
      <Panel title="Developer credentials"
        sub={<>For scripts and CI. A credential (<code>gbe_dev_…</code>) acts as you: it can create games, issue API keys and configure banks, but can't move money. Never put one in a game client.</>}>
        {secret ? (
          <SecretReveal secret={secret} what="credential">
            <Button onClick={() => setSecret(null)}>Done</Button>
          </SecretReveal>
        ) : (
          <form
            className="cred-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const c = await issue.run();
              if (c) {
                setSecret(c.secret ?? "");
                setLabel("");
                invalidate("dev:credentials");
              }
            }}
          >
            <div>
              <Field label="Label" hint="What uses it, so you can recognise it later.">
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="GitHub Actions deploy" maxLength={80} />
              </Field>
            </div>
            <div>
              <Field label="Expiry">
                <Select value={days} onChange={(e) => setDays(e.target.value)}>
                  {EXPIRY.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                </Select>
              </Field>
            </div>
            <Button type="submit" variant="primary" busy={issue.pending}>Issue credential</Button>
          </form>
        )}
        <ErrorNotice error={issue.error} />
        <p className="muted small" style={{ marginTop: 12 }}>
          To rotate a credential: issue a new one, switch your scripts to it, then revoke the old one.
        </p>
      </Panel>
      <Panel
        title={`Active credentials (${live.length})`}
        actions={dead.length > 0 ? (
          <Button size="small" variant="ghost" onClick={() => setShowRevoked((v) => !v)}>
            {showRevoked ? "Hide revoked and expired" : `Show revoked and expired (${dead.length})`}
          </Button>
        ) : undefined}
        flush
      >
        <ErrorNotice error={list.error} onRetry={list.reload} />
        {list.loading && !list.data && <Loading />}
        {list.data && rows.length === 0 && <p className="muted panel-body">No active credentials. You don't need one unless a script or CI job calls the API as you.</p>}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Label</th><th>Prefix</th><th>Last used</th><th>Expires</th><th>Created</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {rows.map((c) => {
                  const dead = !live.includes(c);
                  return (
                    <tr key={c.id} className={dead ? "muted-row" : undefined}>
                      <td>
                        <div className="cell-title">{c.label} {isCurrent(c) && <span className="badge ok">This session</span>}</div>
                        {dead && <div className="cell-sub">{c.revoked_at ? `Revoked ${relativeTime(c.revoked_at)}` : `Expired ${relativeTime(c.expires_at)}`}</div>}
                      </td>
                      <td className="mono">{c.prefix}</td>
                      <td>{c.last_used_at ? relativeTime(c.last_used_at) : "Never"}</td>
                      <td>{c.expires_at ? relativeTime(c.expires_at) : "Never"}</td>
                      <td>{formatDateTime(c.created_at)}</td>
                      <td className="num">{!dead && <Button size="small" variant="danger" onClick={() => setRevoking(c)}>Revoke</Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <Confirm
        open={!!revoking}
        title="Revoke this credential?"
        body={revoking && isCurrent(revoking)
          ? "You're signed in with this credential, so you'll be signed out straight away. Make sure you have another one first."
          : "Anything using it is refused from the next request. This can't be undone."}
        confirmLabel="Revoke credential"
        danger
        busy={revoke.pending}
        error={revoke.error}
        onConfirm={async () => {
          if (revoking && (await revoke.run(revoking))) {
            setRevoking(null);
            invalidate("dev:credentials");
            toast("Credential revoked");
          }
        }}
        onClose={() => setRevoking(null)}
      />
    </section>
  );
}

function AppearancePanel() {
  const [theme, set] = useState<ThemePref>(getTheme());
  return (
    <section id="appearance" className="settings-section">
      <Panel title="Appearance" sub="Saved in this browser.">
        <Segmented label="Theme" value={theme} onChange={(v) => { set(v); setTheme(v); }}
          options={[{ value: "system", label: "Match my system" }, { value: "light", label: "Light" }, { value: "dark", label: "Dark" }]} />
      </Panel>
    </section>
  );
}

function ClosePanel({ me }: { me: Developer }) {
  const api = developerApi(useClient());
  const { signOut } = useSession();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const close = useAction(() => api.closeAccount({ confirm_email: email.trim(), password: me.has_password ? password : undefined }).then(() => true));
  const ready = email.trim().toLowerCase() === me.contact_email.toLowerCase() && (!me.has_password || password !== "");

  return (
    <section id="close" className="settings-section">
      <Panel title="Close account">
        <div className="row" style={{ alignItems: "flex-start", gap: 16 }}>
          <p className="muted grow" style={{ margin: 0 }}>
            Deletes all your games, revokes every API key and credential, and signs you out everywhere. Ledgers and audit history are
            kept for the record. This can't be undone, but you can sign up again later with the same email.
          </p>
          <Button variant="danger" onClick={() => setOpen(true)}>Close account</Button>
        </div>
      </Panel>
      <Modal open={open} title="Close your account?" onClose={() => setOpen(false)}
        description="Every game stops working straight away: their API keys are revoked and webhooks stop."
        onSubmit={async (e) => {
          e.preventDefault();
          if (!ready) return;
          if (await close.run()) {
            setOpen(false);
            signOut("Your account is closed. Thanks for building with Game Banking Engine.");
            navigate("/login", { replace: true });
          }
        }}
        footer={<><Button onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" variant="danger" busy={close.pending} disabled={!ready}>Close my account</Button></>}>
        <div className="form">
          <Field label={`Type ${me.contact_email} to confirm`}>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" spellCheck={false} required />
          </Field>
          {me.has_password && (
            <Field label="Your password">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            </Field>
          )}
          <ErrorNotice error={close.error} />
        </div>
      </Modal>
    </section>
  );
}
